import { useEffect, useMemo, useState, useCallback } from "react";
import { ReviResourceApi } from "../../../../api/revi";
import { useAuth } from "../../../../hooks";

const api = new ReviResourceApi();

const normalize = (r) => ({
  id: r._id,
  title: r.title || "",
  description: r.description || "",
  type: r.type,
  url: r.url || "",
  file: r.file || "",
  image: r.image || "",
  createdAt: r.created_at,
  raw: r,
});

/**
 * Controller de la pagina de Formacion.
 * @param {string} audience - 'therapist' | 'institution'. Filtra el material
 *   por destinatario, de modo que la misma pagina sirve al portal del
 *   terapeuta y al de la institucion REVI.
 */
export function useTherapistDocumentacionController(audience = "therapist") {
  const { accessToken } = useAuth();

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  // Debounce ~350ms (patrón ya usado en el proyecto)
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const [videoPage, setVideoPage] = useState(1);
  const [pdfPage, setPdfPage] = useState(1);

  const videoLimit = 10;
  const pdfLimit = 10;

  const [videos, setVideos] = useState({
    items: [],
    loading: false,
    error: null,
    pagination: null,
  });
  const [pdfs, setPdfs] = useState({
    items: [],
    loading: false,
    error: null,
    pagination: null,
  });
  const [infos, setInfos] = useState({
    items: [],
    loading: false,
    error: null,
  });

  const [preview, setPreview] = useState({
    open: false,
    type: null,
    item: null,
  });

  // reset paginación al buscar
  useEffect(() => {
    setVideoPage(1);
    setPdfPage(1);
  }, [search]);

  const fetchVideos = useCallback(async () => {
    if (!accessToken) return;
    setVideos((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await api.getResources(accessToken, {
        page: videoPage,
        limit: videoLimit,
        type: "video",
        search,
        audience,
      });
      setVideos({
        items: (res.resources || []).map(normalize),
        loading: false,
        error: null,
        pagination: res.pagination || null,
      });
    } catch (e) {
      setVideos((s) => ({
        ...s,
        loading: false,
        error: e?.message || "Error cargando vídeos",
      }));
    }
  }, [accessToken, search, videoPage, audience]);

  const fetchPdfs = useCallback(async () => {
    if (!accessToken) return;
    setPdfs((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await api.getResources(accessToken, {
        page: pdfPage,
        limit: pdfLimit,
        type: "pdf",
        search,
        audience,
      });
      setPdfs({
        items: (res.resources || []).map(normalize),
        loading: false,
        error: null,
        pagination: res.pagination || null,
      });
    } catch (e) {
      setPdfs((s) => ({
        ...s,
        loading: false,
        error: e?.message || "Error cargando PDFs",
      }));
    }
  }, [accessToken, search, pdfPage, audience]);

  const fetchInfos = useCallback(async () => {
    if (!accessToken) return;
    setInfos((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await api.getResources(accessToken, {
        page: 1,
        limit: 1, // ✅ único por audiencia
        type: "info",
        search,
        audience,
      });

      setInfos({
        items: (res.resources || []).map(normalize),
        loading: false,
        error: null,
      });
    } catch (e) {
      setInfos((s) => ({
        ...s,
        loading: false,
        error: e?.message || "Error cargando info",
      }));
    }
  }, [accessToken, search, audience]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  useEffect(() => {
    fetchPdfs();
  }, [fetchPdfs]);

  useEffect(() => {
    fetchInfos();
  }, [fetchInfos]);

  const actions = useMemo(
    () => ({
      setSearchInput,
      setVideoPage,
      setPdfPage,
      openPreview: (type, item) => setPreview({ open: true, type, item }),
      closePreview: () => setPreview({ open: false, type: null, item: null }),
    }),
    [],
  );

  return {
    accessToken,
    searchInput,
    search,
    videoPage,
    pdfPage,
    videoLimit,
    pdfLimit,
    videos,
    pdfs,
    infos,
    preview,
    actions,
  };
}
